
import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, Users, Clock, MapPin, LogIn, LogOut } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';

interface SessionLog {
  id: string;
  employee_id: string;
  employee_name: string;
  session_start: string;
  session_end: string | null;
  session_duration: number | null;
  ip_address: string;
  user_agent: string;
  location_info: any;
  logout_type: string;
  created_at: string;
}

export default function SessionLogs() {
  const [logs, setLogs] = useState<SessionLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const { toast } = useToast();

  const fetchLogs = async () => {
    try {
      let query = supabase
        .from('session_logs')
        .select('*')
        .order('session_start', { ascending: false })
        .limit(100);

      if (filterStatus === 'active') {
        query = query.is('session_end', null);
      } else if (filterStatus === 'ended') {
        query = query.not('session_end', 'is', null);
      }

      if (searchTerm) {
        query = query.ilike('employee_name', `%${searchTerm}%`);
      }

      const { data, error } = await query;

      if (error) throw error;
      setLogs(data || []);
    } catch (error) {
      console.error('Error fetching session logs:', error);
      toast({
        title: "خطأ",
        description: "حدث خطأ في تحميل سجل الجلسات",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [searchTerm, filterStatus]);

  const getSessionStatus = (log: SessionLog) => {
    if (!log.session_end) {
      return { text: 'نشط', color: 'bg-green-100 text-green-800' };
    }
    return { text: 'منتهي', color: 'bg-gray-100 text-gray-800' };
  };

  const formatDuration = (minutes: number | null) => {
    if (!minutes) return 'غير محدد';
    
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours > 0) {
      return `${hours} ساعة و ${mins} دقيقة`;
    }
    return `${mins} دقيقة`;
  };

  const getLogoutTypeText = (type: string) => {
    switch (type) {
      case 'manual': return 'يدوي';
      case 'timeout': return 'انتهاء وقت';
      case 'forced': return 'قسري';
      default: return type;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">سجل الجلسات</h1>
          <p className="text-gray-600">تتبع جلسات دخول وخروج الموظفين</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            فلترة الجلسات
          </CardTitle>
          <CardDescription>
            استخدم الفلاتر للبحث في سجل الجلسات
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="البحث في اسم الموظف..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pr-10"
              />
            </div>
            
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger>
                <SelectValue placeholder="حالة الجلسة" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الجلسات</SelectItem>
                <SelectItem value="active">الجلسات النشطة</SelectItem>
                <SelectItem value="ended">الجلسات المنتهية</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>سجل الجلسات</CardTitle>
          <CardDescription>
            آخر {logs.length} جلسة في النظام
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>الموظف</TableHead>
                    <TableHead>الحالة</TableHead>
                    <TableHead>بداية الجلسة</TableHead>
                    <TableHead>نهاية الجلسة</TableHead>
                    <TableHead>المدة</TableHead>
                    <TableHead>نوع الخروج</TableHead>
                    <TableHead>عنوان IP</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => {
                    const status = getSessionStatus(log);
                    return (
                      <TableRow key={log.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-gray-500" />
                            <span className="font-medium">{log.employee_name}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={status.color}>
                            {status.text}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm">
                            <LogIn className="w-3 h-3 text-green-600" />
                            {format(new Date(log.session_start), 'dd/MM/yyyy HH:mm', { locale: ar })}
                          </div>
                        </TableCell>
                        <TableCell>
                          {log.session_end ? (
                            <div className="flex items-center gap-1 text-sm">
                              <LogOut className="w-3 h-3 text-red-600" />
                              {format(new Date(log.session_end), 'dd/MM/yyyy HH:mm', { locale: ar })}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-500">لا يزال نشط</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm">
                            <Clock className="w-3 h-3 text-blue-600" />
                            {formatDuration(log.session_duration)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">{getLogoutTypeText(log.logout_type)}</span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            <MapPin className="w-3 h-3" />
                            {log.ip_address || 'غير محدد'}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              
              {logs.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  لا توجد جلسات مسجلة
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
